import React, { useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-hot-toast';
import { Link, useLocation } from 'react-router-dom';
import { makeSlug } from '@/utils/slug';
import {
  menuItems,
  menuParticipationItems,
  adminMenuItems,
} from '@/config/menuConfig';

const Sidebar = () => {
  const { globalData, logout } = useAuth();
  const menuRef = useRef(null);
  const { pathname } = useLocation();

  const isItemActive = (item) => {
    if (!item?.link) return false;
    return pathname === item.link || pathname.startsWith(item.link + '/');
  };

  const hasActiveDescendant = (item) => {
    if (!item?.children) return false;
    return item.children.some((c) => isItemActive(c) || hasActiveDescendant(c));
  };

  /* ---------- DexignLab-style accordion open/close ---------- */
  useEffect(() => {
    const root = menuRef.current;
    if (!root) return;

    // cleanup any stale classes
    root
      .querySelectorAll('.mm-active')
      .forEach((n) => n.classList.remove('mm-active'));
    root
      .querySelectorAll('.mm-show')
      .forEach((n) => n.classList.remove('mm-show'));
    root
      .querySelectorAll('a.has-arrow[aria-expanded]')
      .forEach((a) => a.setAttribute('aria-expanded', 'false'));

    const handleParentToggle = (e) => {
      const a = e.target.closest('a.has-arrow');
      if (!a || !root.contains(a)) return;

      e.preventDefault();
      e.stopPropagation();

      const li = a.closest('li');
      const submenu = li?.querySelector(':scope > ul');
      if (!submenu) return;

      const open = submenu.classList.contains('mm-show');

      // Close all (true accordion)
      root
        .querySelectorAll('ul.mm-show')
        .forEach((ul) => ul.classList.remove('mm-show'));
      root
        .querySelectorAll('.mm-active')
        .forEach((el) => el.classList.remove('mm-active'));
      root
        .querySelectorAll('a.has-arrow[aria-expanded="true"]')
        .forEach((x) => x.setAttribute('aria-expanded', 'false'));

      if (!open) {
        submenu.classList.add('mm-show');
        li.classList.add('mm-active');
        a.setAttribute('aria-expanded', 'true');

        // Mark ancestors open
        let parentUl = li.closest('ul');
        while (parentUl && parentUl !== root) {
          const pli = parentUl.closest('li');
          if (pli) {
            pli.classList.add('mm-active');
            parentUl.classList.add('mm-show');
            const ptrig = pli.querySelector(':scope > a.has-arrow');
            if (ptrig) ptrig.setAttribute('aria-expanded', 'true');
          }
          parentUl = pli?.closest('ul');
        }
      }
    };

    root.addEventListener('click', handleParentToggle, true);
    return () => root.removeEventListener('click', handleParentToggle, true);
  }, []);

  /* ---------- ensure current route’s ancestors are opened ---------- */
  useEffect(() => {
    const root = menuRef.current;
    if (!root) return;

    root
      .querySelectorAll('.mm-show')
      .forEach((n) => n.classList.remove('mm-show'));
    root
      .querySelectorAll('.mm-active')
      .forEach((n) => n.classList.remove('mm-active'));
    root
      .querySelectorAll('a.has-arrow[aria-expanded]')
      .forEach((a) => a.setAttribute('aria-expanded', 'false'));

    const activeLink =
      root.querySelector(`a[href="${pathname}"]`) ||
      root.querySelector(`a[href="${pathname.replace(/\/+$/, '')}"]`);
    if (!activeLink) return;

    const leafLi = activeLink.closest('li');
    let parentUl = leafLi?.closest('ul');

    leafLi?.classList.add('mm-active');

    while (parentUl && parentUl !== root) {
      parentUl.classList.add('mm-show');
      const parentLi = parentUl.closest('li');
      if (parentLi) {
        parentLi.classList.add('mm-active');
        const trig = parentLi.querySelector(':scope > a.has-arrow');
        if (trig) trig.setAttribute('aria-expanded', 'true');
      }
      parentUl = parentLi?.closest('ul');
    }
  }, [pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('User logged out successfully.');
    } catch (error) {
      console.error('Logout Error:', error?.message || error);
    }
  };

  const resetAllTours = () => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('tour-dismissed-')) localStorage.removeItem(key);
    });
    toast.success('All tours have been reset!');
  };

  const navigationMenu =
    globalData?.accountType === 'User'
      ? [...menuItems, ...(globalData?.isAdmin ? adminMenuItems : [])]
      : menuParticipationItems;

  const groupedMenu = navigationMenu.reduce((acc, item) => {
    const group = item.menu || 'Others';
    (acc[group] ||= []).push(item);
    return acc;
  }, {});

  /* ---------- recursive item renderer (supports grandchildren) ---------- */
  function renderItem(item, keyPrefix = '') {
    const key = item.link || item.name || keyPrefix;
    const hasChildren =
      Array.isArray(item.children) && item.children.length > 0;
    const active = isItemActive(item);
    const open = active || hasActiveDescendant(item);

    if (hasChildren) {
      return (
        <li key={key} className={open ? 'mm-active' : ''}>
          <a
            className='has-arrow'
            href='javascript:void(0)'
            aria-expanded={open ? 'true' : 'false'}
            role='button'
            tabIndex={0}
          >
            {item.icon && <i className={item.icon} />}
            <span className='nav-text'>{item.name}</span>
          </a>
          <ul
            aria-expanded={open ? 'true' : 'false'}
            className={open ? 'mm-show' : ''}
          >
            {item.children.map((child, idx) =>
              renderItem(child, `${key}-${idx}`)
            )}
          </ul>
        </li>
      );
    }

    return (
      <li key={key} className={active ? 'mm-active' : ''}>
        <Link to={item.link} className={active ? 'active' : ''}>
          {item.icon && <i className={item.icon} />}
          <span className='nav-text ms-2'>{item.name}</span>
          {item.notification && (
            <span className='badge rounded-pill bg-danger ms-2'>
              {item.notification}
            </span>
          )}
        </Link>
      </li>
    );
  }

  /* ---------- group renderer (uses the recursive item renderer) ---------- */
  const renderGroupedMenu = (menuGroups) =>
    Object.entries(menuGroups).map(([group, items]) => {
      const groupId = `tour-sidebar-group-${makeSlug(group)}`;
      const groupOpen = items.some(
        (it) => isItemActive(it) || hasActiveDescendant(it)
      );
      const groupIcon = items.find((i) => i.icon)?.icon || 'fas fa-ellipsis-h';

      return (
        <li key={group}>
          <a
            id={groupId}
            className='has-arrow'
            href='javascript:void(0)'
            aria-expanded={groupOpen ? 'true' : 'false'}
            role='button'
            tabIndex={0}
            data-tour={groupId}
          >
            <i className={groupIcon} />
            <span className='nav-text'>{group}</span>
          </a>
          <ul
            aria-expanded={groupOpen ? 'true' : 'false'}
            className={groupOpen ? 'mm-show' : ''}
          >
            {items.map((item, index) => renderItem(item, `${group}-${index}`))}
          </ul>
        </li>
      );
    });

  return (
    <div className='dlabnav' id='tour-sidebar'>
      <div className='dlabnav-scroll'>
        <ul
          className='metismenu'
          id='menu'
          ref={menuRef}
          data-react-managed='true'
        >
          {renderGroupedMenu(groupedMenu)}
          <li>
            <a
              id='tour-reset-tours'
              onClick={(e) => {
                e.preventDefault();
                resetAllTours();
              }}
            >
              <i className='fas fa-walking'></i>
              <span className='nav-text'>Reset All Tours</span>
            </a>
          </li>
          <li>
            <a
              id='tour-logout'
              onClick={(e) => {
                e.preventDefault();
                handleLogout();
              }}
            >
              <i className='fas fa-power-off'></i>
              <span className='nav-text'>Logout</span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;
